import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TelnyxRTC } from "@telnyx/webrtc";
import { routeDestination } from "@/lib/sipTrunk";
import api, { apiError } from "@/lib/api";
import { createRingback } from "@/lib/ringback";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { Link, useNavigate } from "react-router-dom";
import { IyenehContacts } from "@/components/IyenehContacts";
import { PushNudge } from "@/components/PushNudge";
import { InstallButton } from "@/components/PWAInstall";
import {
  Phone, PhoneSlash, Backspace, Star, Plus, GearSix, Path, GlobeHemisphereEast, Users,
  ArrowUpRight, ArrowDownLeft, Microphone, MicrophoneSlash, X, Pause, Play, VideoCamera, LinkSimple, DotsNine, Record, SpeakerHigh, SpeakerSlash, DownloadSimple,
} from "@phosphor-icons/react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const LETTERS = { "2": "ABC", "3": "DEF", "4": "GHI", "5": "JKL", "6": "MNO", "7": "PQRS", "8": "TUV", "9": "WXYZ" };
const MODES = [
  { id: "economy", label: "Economy" },
  { id: "balanced", label: "Balanced" },
  { id: "premium", label: "Premium" },
];

const toE164 = (s) => "+" + ((s || "").match(/\d/g) || []).join("");

export default function Call() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user && ["platform_owner", "admin"].includes(user.role);
  const callCtx = useCall();
  const [callMode, setCallMode] = useState("phone");
  const [num, setNum] = useState("");
  const [mode, setMode] = useState("balanced");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rate, setRate] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [active, setActive] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [callState, setCallState] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [incoming, setIncoming] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newC, setNewC] = useState({ name: "", number: "", favorite: false });
  const [dtmfLog, setDtmfLog] = useState("");
  const [recording, setRecording] = useState(false);
  const [showInCallKeys, setShowInCallKeys] = useState(false);
  const [speaker, setSpeaker] = useState(true);

  const timer = useRef(null);
  const elapsedRef = useRef(0);
  const clientRef = useRef(null);
  const callRef = useRef(null);
  const audioRef = useRef(null);
  const callerRef = useRef("");
  const activeRef = useRef(null);
  const incomingRef = useRef(null);
  const recRef = useRef(null);
  const recChunksRef = useRef([]);
  const ringbackRef = useRef(null);
  if (!ringbackRef.current) ringbackRef.current = createRingback();

  const { data: contactsData } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: history } = useQuery({ queryKey: ["calls"], queryFn: async () => (await api.get("/calling/history")).data });
  const { data: myDids } = useQuery({ queryKey: ["dids-mine"], queryFn: async () => (await api.get("/dids/mine")).data });
  const { data: speedDial } = useQuery({ queryKey: ["speed-dial"], queryFn: async () => (await api.get("/pbx/speed-dial")).data });
  const { data: callerIds } = useQuery({ queryKey: ["caller-ids"], queryFn: async () => (await api.get("/pbx/caller-ids")).data });
  const { data: myExtData } = useQuery({ queryKey: ["my-extension"], queryFn: async () => (await api.get("/pbx/my-extension")).data, refetchInterval: 30000 });
  const myExtension = myExtData?.extension;
  const addSpeedDial = async () => {
    const v = num.trim(); if (!v) return;
    try { await api.post("/pbx/speed-dial", { value: v, label: "" }); qc.invalidateQueries({ queryKey: ["speed-dial"] }); toast.success("Saved to speed dial"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeCallerId = async (n) => {
    try { await api.post("/pbx/caller-id", { number: n }); qc.invalidateQueries({ queryKey: ["caller-ids"] }); toast.success("Caller ID updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const hasNumber = (myDids?.dids?.length || 0) > 0;
  const contacts = contactsData?.contacts || [];
  const favorites = contacts.filter((c) => c.favorite);
  const recents = (history?.calls || []).slice(0, 8);

  useEffect(() => {
    const v = num.trim();
    if (!v) { setRate(null); setResolved(null); return; }
    const isExt = !v.includes("+") && /^\d{5}$/.test(v);
    const t = setTimeout(async () => {
      if (isExt) {
        try { const { data } = await api.post("/pbx/dial-resolve", { input: v }); setResolved(data); setRate(null); }
        catch { setResolved(null); }
      } else {
        setResolved(null);
        try { const { data } = await api.post("/calling/rate", { number: v, mode }); setRate(data); }
        catch { setRate(null); }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [num, mode]);

  useEffect(() => () => {
    clearInterval(timer.current);
    try { if (callRef.current) callRef.current.hangup(); } catch {}
    try { if (clientRef.current) clientRef.current.disconnect(); } catch {}
    try { ringbackRef.current?.stop(); } catch {}
  }, []);

  const press = (k) => setNum((n) => n + k);
  const del = () => setNum((n) => n.slice(0, -1));
  const zeroTimer = useRef(null);
  const zeroPlus = useRef(false);
  const start0 = () => { zeroPlus.current = false; zeroTimer.current = setTimeout(() => { zeroPlus.current = true; setNum((n) => n + "+"); }, 450); };
  const end0 = () => { clearTimeout(zeroTimer.current); if (!zeroPlus.current) press("0"); };

  const ensureClient = async () => {
    if (clientRef.current && clientRef.current.connected) return clientRef.current;
    const { data } = await api.post("/calling/webrtc-token");
    callerRef.current = data.caller_number || "";
    const client = new TelnyxRTC({ login_token: data.token });
    client.on("telnyx.notification", (n) => {
      if (n.type !== "callUpdate" || !n.call) return;
      const call = n.call;
      const st = call.state;
      if (st === "ringing" && call.direction === "inbound" && !activeRef.current) {
        incomingRef.current = call;
        setIncoming({ from: call.options?.remoteCallerNumber || call.remoteCallerNumber || "Unknown" });
        return;
      }
      if (callRef.current && call.id !== callRef.current.id) return;
      setCallState(st);
      if (call.remoteStream && audioRef.current) audioRef.current.srcObject = call.remoteStream;
      if (["new", "requesting", "trying", "ringing", "early"].includes(st)) { try { ringbackRef.current?.start(); } catch {} }
      if (["active", "held"].includes(st)) { try { ringbackRef.current?.stop(); } catch {} }
      if (st === "hangup" || st === "destroy") { try { ringbackRef.current?.stop(); } catch {} finalizeCall(true); }
    });
    await new Promise((resolve, reject) => {
      const to = setTimeout(() => reject(new Error("Calling service timed out")), 12000);
      client.on("telnyx.ready", () => { clearTimeout(to); resolve(); });
      client.on("telnyx.error", (e) => { clearTimeout(to); reject(e?.error || e || new Error("WebRTC error")); });
      client.connect();
    });
    clientRef.current = client;
    return client;
  };

  useEffect(() => { ensureClient().catch(() => {}); }, []);

  const handleDial = async () => {
    const v = num.trim();
    if (!v) return;
    try {
      const { data } = await api.post("/pbx/dial-resolve", { input: v });
      if (data.kind === "internal") {
        const fa = data.forwarding?.always;
        if (data.member?.user_id) { callCtx?.startCall?.(data.member.user_id, false); toast.success(`Calling ${data.name} · ext ${data.ext}`); }
        else if (fa?.enabled && fa.type === "ai_service") {
          if (fa.target === "sales") { toast.success(`Connecting you to ${data.name}…`); navigate("/app/ai-sales"); }
          else { toast.success(`Connecting you to ${data.name}…`); navigate("/book"); }
        }
        else if (data.fallback_type === "forward" && data.fallback_number) { toast(`${data.name} unavailable — forwarding`); startCall(data.fallback_number); }
        else toast(`${data.name} isn't reachable right now — try messaging them.`);
        return;
      }
      if (data.kind === "ring_group") {
        const ids = data.member_user_ids || [];
        if (ids.length === 0) { toast.error(`${data.name} has no reachable members yet`); return; }
        if (data.strategy === "ring_all" || ids.length === 1) { callCtx?.startGroupCall?.(ids, false); toast.success(`Ringing ${data.name}…`); }
        else { const on = (data.members || []).find((m) => m.online && m.user_id); callCtx?.startCall?.((on?.user_id) || ids[0], false); toast.success(`Ringing ${data.name}…`); }
        return;
      }
      if (data.kind === "external") {
        if (data.allowed === false) { toast.error(data.reason || "Calling not permitted"); return; }
        startCall(data.e164);
        return;
      }
      if (data.kind === "meet") {
        toast.success(`Joining ${data.name}…`);
        navigate(`/app/meet?room=${data.room_code}`);
        return;
      }
      toast.error(data.reason || "Invalid number or extension");
    } catch (e) { toast.error(apiError(e)); }
  };

  const startCall = async (destOverride) => {
    const dest = destOverride || toE164(num);
    if (dest.length < 8) { toast.error("Enter a valid number in international format"); return; }
    setConnecting(true);
    try {
      const client = await ensureClient();
      const { data } = await api.post("/calling/start", { number: dest, mode });
      setActive(data); activeRef.current = data;
      setElapsed(0); elapsedRef.current = 0; setMuted(false);
      const opts = { destinationNumber: await routeDestination(dest), audio: true, video: false };
      const caller = toE164(data.call.caller_id || callerRef.current || "");
      if (caller.length > 5) opts.callerNumber = caller;
      callRef.current = client.newCall(opts);
      try { ringbackRef.current?.start(); } catch {}
      timer.current = setInterval(() => setElapsed((e) => { elapsedRef.current = e + 1; return e + 1; }), 1000);
    } catch (e) {
      const msg = typeof e === "string" ? e : (e?.message || apiError(e) || "Could not start call");
      toast.error(msg);
      setActive(null); activeRef.current = null;
    } finally { setConnecting(false); }
  };

  // shared teardown + billing; fromRemote avoids double-hangup
  const finalizeCall = async (fromRemote = false) => {
    if (!activeRef.current) return;
    const current = activeRef.current;
    activeRef.current = null;
    try { ringbackRef.current?.stop(); } catch {}
    clearInterval(timer.current);
    if (!fromRemote) { try { callRef.current && callRef.current.hangup(); } catch {} }
    const dur = elapsedRef.current;
    if (current.inbound) {
      toast.success("Call ended");
    } else {
      try {
        const { data } = await api.post("/calling/end", { call_id: current.call.id, duration_seconds: dur });
        toast.success(`Call ended · billed ${money(data.call.cost_cents)}`);
      } catch (e) { toast.error(apiError(e)); }
    }
    callRef.current = null;
    try { recRef.current?.stop(); } catch {} recRef.current = null;
    setRecording(false); setShowInCallKeys(false); setDtmfLog("");
    setActive(null); setElapsed(0); setCallState(""); setHeld(false);
    if (audioRef.current) audioRef.current.srcObject = null;
    qc.invalidateQueries({ queryKey: ["wallet"] });
    qc.invalidateQueries({ queryKey: ["calls"] });
  };

  const toggleMute = () => {
    const c = callRef.current; if (!c) return;
    try { muted ? c.unmuteAudio() : c.muteAudio(); setMuted((m) => !m); } catch {}
  };

  const toggleHold = () => {
    const c = callRef.current; if (!c) return;
    try { held ? c.unhold() : c.hold(); setHeld((h) => !h); } catch {}
  };

  // Send a DTMF tone during an active call (navigate IVR menus, enter extensions/PINs).
  const sendDtmf = (k) => {
    const c = callRef.current; if (!c) return;
    try { c.dtmf(k); setDtmfLog((d) => (d + k).slice(-24)); } catch {}
  };

  // Record the live call locally (mixes the caller's audio + your mic) and download it.
  const startRecording = async () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      [callRef.current?.remoteStream, callRef.current?.localStream].forEach((s) => {
        try { if (s && s.getAudioTracks && s.getAudioTracks().length) ctx.createMediaStreamSource(s).connect(dest); } catch {}
      });
      const rec = new MediaRecorder(dest.stream);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) recChunksRef.current.push(e.data); };
      rec.onstop = () => {
        try {
          const blob = new Blob(recChunksRef.current, { type: "audio/webm" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `iyeneh-call-${Date.now()}.webm`; a.click();
          URL.revokeObjectURL(url);
        } catch {}
        try { ctx.close(); } catch {}
      };
      rec.start(); recRef.current = rec; setRecording(true); toast.success("Recording started");
    } catch { toast.error("Recording isn't supported on this device"); }
  };
  const stopRecording = () => { try { recRef.current?.stop(); } catch {} recRef.current = null; setRecording(false); toast.success("Recording saved"); };
  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  // Speakerphone: route call audio loud through speakers (setSinkId when supported) vs. quieter earpiece level.
  const toggleSpeaker = async () => {
    const a = audioRef.current; if (!a) return;
    const on = !speaker; setSpeaker(on);
    try { a.volume = on ? 1.0 : 0.45; } catch {}
    try { if (on && a.setSinkId) await a.setSinkId("default"); } catch {}
  };

  const answerIncoming = () => {
    const call = incomingRef.current; if (!call) return;
    try { call.answer(); } catch {}
    callRef.current = call;
    const from = incoming?.from || "Incoming";
    const rec = { call: { id: call.id, number: from, destination: "Inbound call", caller_id: callerRef.current, carrier: "iYeneh Network", retail_cpm: 0 }, inbound: true };
    setActive(rec); activeRef.current = rec;
    setElapsed(0); elapsedRef.current = 0; setMuted(false); setHeld(false);
    setIncoming(null); incomingRef.current = null;
    timer.current = setInterval(() => setElapsed((e) => { elapsedRef.current = e + 1; return e + 1; }), 1000);
  };

  const rejectIncoming = () => {
    try { incomingRef.current && incomingRef.current.hangup(); } catch {}
    setIncoming(null); incomingRef.current = null;
  };

  const addContact = async () => {
    if (!newC.name || !newC.number) return;
    try {
      await api.post("/contacts", newC);
      toast.success("Contact saved");
      setShowAdd(false); setNewC({ name: "", number: "", favorite: false });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const prettyState = (s) => ({ new: "Dialing…", requesting: "Dialing…", trying: "Dialing…", ringing: "Ringing…", active: "Connected", held: "On hold" }[s] || (s ? s : "Connecting…"));

  return (
    <div className="space-y-6">
      <audio ref={audioRef} autoPlay data-testid="call-audio" className="hidden" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Global Calling</h1>
          <p className="mt-2 text-muted-foreground">Live browser calling to 200+ countries worldwide with smart least-cost routing.</p>
          {!isStaff && hasNumber && (
            <span data-testid="reachable-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Reachable at {myDids.dids[0].number}
            </span>
          )}
        </div>
      </div>

      <PushNudge />

      {!isStaff && myDids && !hasNumber && (
        <div data-testid="call-numbers-nudge" className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#059669]/30 bg-[#059669]/5 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#059669] text-white">
              <GlobeHemisphereEast size={20} weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-semibold">Get your own number for a real caller ID</p>
              <p className="mt-0.5 text-xs text-muted-foreground">You can call out now — add a number to receive calls &amp; texts and show your own caller ID.</p>
            </div>
          </div>
          <Link to="/app/numbers" data-testid="call-numbers-cta"
            className="flex items-center gap-2 rounded-full bg-[#059669] px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-105">
            <Plus size={15} weight="bold" /> Get a number
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between gap-2" data-testid="call-mode-tabs">
        <div className="flex min-w-0 gap-2">
          <button data-testid="callmode-phone" onClick={() => setCallMode("phone")}
            className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition-colors sm:px-4 sm:text-sm ${callMode === "phone" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>Phone (paid)</button>
          <button data-testid="callmode-free" onClick={() => setCallMode("free")}
            className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition-colors sm:px-4 sm:text-sm ${callMode === "free" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>Internet call</button>
        </div>
        <button onClick={() => navigate("/dialer")} data-testid="dialer-download-dialer" className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-3 py-2 text-xs font-bold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:px-4 sm:py-2.5 sm:text-sm">
          <DownloadSimple size={16} weight="bold" className="hidden sm:inline" /> Download iYeneh Dialer
        </button>
      </div>

      {callMode === "free" ? (
        <FreeCallPanel call={callCtx} contacts={contacts} />
      ) : (
      <div className="grid gap-6 lg:grid-cols-5">
        {/* dialer */}
        <div className="lg:col-span-3">
          {active ? (
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#08111F] p-8 text-center text-white">
              <div className="grain absolute inset-0" />
              <div className="relative">
                <p data-testid="call-state" className={`text-xs uppercase tracking-[0.2em] ${callState === "active" ? "text-emerald-400" : "text-amber-300"}`}>● {prettyState(callState)} · {active.call.carrier}</p>
                <p className="mt-4 font-mono-metric text-2xl">{active.call.number}</p>
                <p className="text-sm text-white/50">{active.call.destination} · caller ID {active.call.caller_id}</p>
                <p className="mt-6 font-mono-metric text-6xl font-semibold">{fmtDur(elapsed)}</p>
                <p className="mt-2 text-sm text-white/50">Est. {money(Math.ceil(active.call.retail_cpm * elapsed / 60))} @ {active.call.retail_cpm}¢/min</p>
                <div className="mt-8 flex items-center justify-center gap-5">
                  <button data-testid="mute-call" onClick={toggleMute}
                    className={`flex h-14 w-14 items-center justify-center rounded-full border transition-transform hover:scale-105 ${muted ? "border-amber-400 bg-amber-400/20 text-amber-300" : "border-white/20 text-white"}`}>
                    {muted ? <MicrophoneSlash size={24} weight="fill" /> : <Microphone size={24} weight="fill" />}
                  </button>
                  <button data-testid="hold-call" onClick={toggleHold}
                    className={`flex h-14 w-14 items-center justify-center rounded-full border transition-transform hover:scale-105 ${held ? "border-amber-400 bg-amber-400/20 text-amber-300" : "border-white/20 text-white"}`}>
                    {held ? <Play size={22} weight="fill" /> : <Pause size={22} weight="fill" />}
                  </button>
                  <button data-testid="speaker-call" onClick={toggleSpeaker}
                    className={`flex h-14 w-14 items-center justify-center rounded-full border transition-transform hover:scale-105 ${speaker ? "border-[#00A6F7] bg-[#00A6F7]/20 text-[#00A6F7] shadow-[0_0_14px_rgba(0,166,247,0.45)]" : "border-white/20 text-white"}`}>
                    {speaker ? <SpeakerHigh size={24} weight="fill" /> : <SpeakerSlash size={24} weight="fill" />}
                  </button>
                  <button data-testid="record-call" onClick={toggleRecording}
                    className={`flex h-14 w-14 items-center justify-center rounded-full border transition-transform hover:scale-105 ${recording ? "border-red-500 bg-red-500/20 text-red-400 animate-pulse" : "border-white/20 text-white"}`}>
                    <Record size={24} weight="fill" />
                  </button>
                  <button data-testid="keypad-toggle" onClick={() => setShowInCallKeys((s) => !s)}
                    className={`flex h-14 w-14 items-center justify-center rounded-full border transition-transform hover:scale-105 ${showInCallKeys ? "border-[#00A6F7] bg-[#00A6F7]/20 text-[#00A6F7]" : "border-white/20 text-white"}`}>
                    <DotsNine size={24} weight="fill" />
                  </button>
                  <button data-testid="end-call" onClick={() => finalizeCall(false)}
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E53935] shadow-[0_8px_24px_rgba(229,57,53,0.45)] transition-transform hover:scale-105">
                    <PhoneSlash size={28} weight="fill" />
                  </button>
                </div>
                {showInCallKeys && (
                  <div className="mt-6" data-testid="incall-keypad">
                    {dtmfLog && <p className="mb-2 font-mono-metric text-lg tracking-widest text-[#00A6F7]">{dtmfLog}</p>}
                    <div className="mx-auto grid max-w-[260px] grid-cols-3 gap-2.5">
                      {KEYS.map((k) => (
                        <button key={k} data-testid={`dtmf-${k}`} onClick={() => sendDtmf(k)}
                          className="flex h-14 flex-col items-center justify-center rounded-2xl border border-white/15 bg-white/[0.04] font-display text-xl leading-none text-white transition-all hover:bg-white/10 hover:shadow-[0_0_14px_rgba(255,122,0,0.35)] active:scale-95">
                          {k}
                          {LETTERS[k] && <span className="mt-0.5 text-[9px] tracking-widest text-white/40">{LETTERS[k]}</span>}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-center text-[10px] text-white/40">Tap to send tones — navigate phone menus, enter PINs</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0B1F3A] p-6 text-white shadow-[0_20px_60px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)]">
              {/* branded header */}
              <div className="mb-4 border-b border-white/10 pb-4" data-testid="dialer-header">
                <div className="flex items-center justify-center gap-2.5">
                  <img src="/favicon.png?v=5" alt="iYeneh" className="h-7 w-7 object-contain" />
                  <span className="font-display text-base font-semibold tracking-wide text-white">iYeneh Softphone Dialer</span>
                </div>
                {myExtension && (
                  <div className="mt-2.5 flex items-center justify-center" data-testid="my-extension-chip">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#00A6F7]/40 bg-[#00A6F7]/10 px-3 py-1 text-xs font-semibold text-[#00A6F7]">
                      <span className={`h-1.5 w-1.5 rounded-full ${myExtension.online ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" : "bg-white/30"}`} />
                      Your extension {myExtension.ext}{myExtension.name ? ` · ${myExtension.name}` : ""}
                    </span>
                  </div>
                )}
              </div>
              {/* top: speaker grille + status LED — physical-phone feel */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex gap-1">{[0,1,2,3,4,5].map((i)=>(<span key={i} className="h-1 w-1 rounded-full bg-white/15" />))}</div>
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#00A6F7]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00A6F7] shadow-[0_0_8px_2px_rgba(0,166,247,0.75)] animate-pulse" /> Ready
                </span>
              </div>
              {/* screen */}
              <div className="mb-3 rounded-2xl border border-white/10 bg-[#040A12] px-4 py-3 shadow-[inset_0_2px_10px_rgba(0,0,0,0.7)]">
                <input data-testid="dial-input" value={num} onChange={(e) => setNum(e.target.value)}
                  className="w-full bg-transparent text-center font-mono-metric text-3xl text-[#00A6F7] outline-none placeholder:text-white/25"
                  style={{ textShadow: "0 0 12px rgba(0,166,247,0.5)" }} placeholder="Extension or number" />
              </div>

              {/* rate / extension strip */}
              {resolved ? (
                <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3 text-sm" data-testid="dial-resolved-strip">
                  <div>
                    <p className="font-medium">{resolved.kind === "internal" ? `${resolved.name || "Extension"} · ext ${resolved.ext}` : resolved.kind === "ring_group" ? `${resolved.name} · ring group` : (resolved.reason || "Not found")}</p>
                    <p className="text-xs text-white/50">
                      {resolved.kind === "internal"
                        ? (resolved.member?.online ? "Online · in-app call (free)" : "Will ring their app, then fallback")
                        : resolved.kind === "ring_group"
                        ? `${resolved.members?.length || 0} members · ${(resolved.strategy || "").replace(/_/g, " ")}`
                        : "No matching internal extension"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono-metric font-semibold ${resolved.kind === "internal" || resolved.kind === "ring_group" ? "text-emerald-400" : "text-white/50"}`}>{resolved.kind === "internal" || resolved.kind === "ring_group" ? "FREE" : "—"}</p>
                    <p className="text-xs text-white/50">{resolved.kind === "internal" ? resolved.department_label : ""}</p>
                  </div>
                </div>
              ) : (
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3 text-sm">
                <div>
                  <p className="font-medium">{rate ? `${rate.destination.flag} ${rate.destination.name}` : "Enter extension or number"}</p>
                  <p className="text-xs text-white/50">Balance {rate ? money(rate.balance_cents) : "—"}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-metric font-semibold">{rate ? `${rate.retail_cpm}¢/min` : "—"}</p>
                  <p className="text-xs text-white/50">~{rate ? rate.estimated_minutes : 0} min</p>
                </div>
              </div>
              )}

              <div className="mx-auto mt-5 grid max-w-xs grid-cols-3 gap-3">
                {KEYS.map((k) => (
                  k === "0" ? (
                    <button key={k} data-testid="key-0"
                      onPointerDown={start0} onPointerUp={end0} onPointerLeave={() => clearTimeout(zeroTimer.current)}
                      className="relative flex h-16 flex-col items-center justify-center rounded-2xl border border-black/10 bg-white font-display text-2xl leading-none text-[#0B1F3A] shadow-[inset_0_-2px_0_rgba(0,0,0,0.12)] transition-all hover:bg-[#00A6F7] hover:text-white hover:shadow-[0_0_16px_rgba(0,166,247,0.5)] active:scale-95">
                      0<span className="mt-0.5 text-[11px] font-semibold text-[#0B1F3A]/50">+</span>
                    </button>
                  ) : (
                    <button key={k} data-testid={`key-${k}`} onClick={() => press(k)}
                      className="flex h-16 flex-col items-center justify-center rounded-2xl border border-black/10 bg-white font-display text-2xl leading-none text-[#0B1F3A] shadow-[inset_0_-2px_0_rgba(0,0,0,0.12)] transition-all hover:bg-[#00A6F7] hover:text-white hover:shadow-[0_0_16px_rgba(0,166,247,0.5)] active:scale-95">
                      {k}
                      {LETTERS[k] && <span className="mt-0.5 text-[9px] font-semibold tracking-widest text-[#0B1F3A]/50">{LETTERS[k]}</span>}
                    </button>
                  )
                ))}
              </div>
              <p className="mx-auto mt-1.5 max-w-xs text-center text-[10px] text-white/40">Hold <b>0</b> for <b>+</b> (international)</p>

              {speedDial?.speed_dial?.length > 0 && (
                <div className="mx-auto mt-4 max-w-xs" data-testid="speed-dial-row">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/50">Speed dial</p>
                  <div className="flex flex-wrap gap-1.5">
                    {speedDial.speed_dial.map((s) => (
                      <button key={s.id} data-testid={`speed-dial-${s.id}`} onClick={() => setNum(s.value)}
                        className="rounded-full border border-white/15 px-3 py-1 text-xs font-medium text-white/80 hover:border-[#00A6F7] hover:text-[#00A6F7]">
                        {s.label}{s.label !== s.value && <span className="ml-1 font-mono-metric text-white/40">{s.value}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mx-auto mt-5 flex max-w-xs items-center justify-between">
                <button onClick={() => setShowAdvanced((s) => !s)} data-testid="advanced-toggle"
                  className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white"><GearSix size={16} /> Advanced</button>
                <button data-testid="dial-call" onClick={handleDial} disabled={connecting || !num.trim() || (!isStaff && rate && rate.balance_cents <= 0)}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00A86B] shadow-[0_8px_24px_rgba(0,168,107,0.45)] transition-transform hover:scale-105 disabled:opacity-40">
                  {connecting
                    ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    : <Phone size={26} weight="fill" className="text-white" />}
                </button>
                <button data-testid="dial-save-speed" onClick={addSpeedDial} title="Save to speed dial" className="text-white/60 hover:text-[#00A6F7]"><Star size={20} /></button>
                <button data-testid="dial-backspace" onClick={del} className="text-white/60 hover:text-white"><Backspace size={22} /></button>
              </div>

              {showAdvanced && (
                <div className="mx-auto mt-4 max-w-xs rounded-2xl border border-white/15 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/50"><Path size={13} /> Routing mode (LCR)</p>
                  <div className="flex gap-2">
                    {MODES.map((m) => (
                      <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
                        className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-medium ${mode === m.id ? "bg-[#00A6F7] text-white" : "bg-white/10 text-white/70 hover:bg-white/15"}`}>{m.label}</button>
                    ))}
                  </div>
                  {callerIds?.allowed?.length > 0 && (
                    <div className="mt-3" data-testid="caller-id-select">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/50">Outbound caller ID</p>
                      <select data-testid="caller-id-dropdown" value={callerIds.selected || callerIds.allowed[0].number}
                        onChange={(e) => changeCallerId(e.target.value)}
                        className="w-full rounded-lg border border-white/15 bg-white/5 px-2 py-1.5 text-xs font-mono-metric text-white [&>option]:text-black">
                        {callerIds.allowed.map((a) => <option key={a.number} value={a.number}>{a.number} — {a.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* contacts + recents */}
        <div className="lg:col-span-2 space-y-6">
          <Link to="/app/directory" data-testid="call-directory-link"
            className="flex items-center justify-between rounded-2xl border border-primary/30 bg-primary/5 p-4 transition-colors hover:bg-primary/10">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Users size={20} weight="duotone" /></div>
              <div>
                <p className="text-sm font-semibold">Company Directory</p>
                <p className="text-xs text-muted-foreground">Dial a 5-digit extension or find a colleague</p>
              </div>
            </div>
            <ArrowUpRight size={18} className="text-primary" />
          </Link>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Favorites & Contacts</h2>
              <button data-testid="add-contact-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-xs font-semibold text-primary"><Plus size={14} weight="bold" /> Add</button>
            </div>
            {contacts.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No contacts yet</p>}
            <div className="space-y-1">
              {[...favorites, ...contacts.filter((c) => !c.favorite)].slice(0, 8).map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-primary">{c.name.charAt(0).toUpperCase()}</div>
                  <button onClick={() => setNum(c.number)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium">{c.name}{c.favorite && <Star size={12} weight="fill" className="ml-1 inline text-amber-400" />}</p>
                    <p className="truncate font-mono-metric text-xs text-muted-foreground">{c.number}</p>
                  </button>
                  <button onClick={() => setNum(c.number)} className="text-primary"><Phone size={18} weight="duotone" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent calls</h2>
            {recents.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No calls yet</p>}
            <div className="space-y-1">
              {recents.map((c) => (
                <button key={c.id} onClick={() => setNum(c.number)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-secondary">
                  <ArrowUpRight size={16} className="text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono-metric text-sm">{c.number}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.destination} · {shortDate(c.started_at)}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{c.duration_seconds != null ? `${c.duration_seconds}s` : ""} · {money(c.cost_cents || 0)}</span>
                </button>
              ))}
            </div>
          </div>

          <IyenehContacts smsEnabled />
        </div>
      </div>
      )}

      {incoming && (
        <div className="fixed inset-x-0 top-4 z-50 mx-auto w-full max-w-sm rounded-2xl border border-primary bg-card p-4 shadow-xl" data-testid="incoming-call">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Incoming call</p>
          <p className="mt-1 font-mono-metric text-lg">{incoming.from}</p>
          <div className="mt-3 flex gap-3">
            <button data-testid="answer-call" onClick={answerIncoming} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-500 py-2.5 font-semibold text-white"><Phone size={18} weight="fill" /> Answer</button>
            <button data-testid="reject-call" onClick={rejectIncoming} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-destructive py-2.5 font-semibold text-white"><PhoneSlash size={18} weight="fill" /> Reject</button>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Add contact</h3><button onClick={() => setShowAdd(false)}><X size={18} /></button></div>
            <input data-testid="contact-name" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} placeholder="Name" className="mb-2 w-full rounded-xl border border-input bg-card px-3 py-2.5 outline-none focus:border-primary" />
            <input data-testid="contact-number" value={newC.number} onChange={(e) => setNewC({ ...newC, number: e.target.value })} placeholder="+1 …" className="mb-3 w-full rounded-xl border border-input bg-card px-3 py-2.5 font-mono-metric outline-none focus:border-primary" />
            <label className="mb-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={newC.favorite} onChange={(e) => setNewC({ ...newC, favorite: e.target.checked })} /> Mark as favorite</label>
            <button data-testid="save-contact" onClick={addContact} className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">Save contact</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FreeCallPanel({ call, contacts }) {
  const [recipient, setRecipient] = useState("");
  const { data: settings, isLoading: rateLoading } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data });
  const { data: history = [] } = useQuery({ queryKey: ["call-history"], queryFn: async () => (await api.get("/calls/history")).data.calls, refetchInterval: 20000 });
  const cpm = Number(settings?.internet_call_rate_cpm ?? 0);
  const rateReady = !rateLoading && !!settings;
  const rateLabel = !rateReady ? "…" : (cpm > 0 ? `${cpm}¢/min` : "Free");
  const start = (video) => {
    if (!recipient.trim()) { toast.error("Enter a member's phone number or email"); return; }
    call?.startCall?.(recipient.trim(), video);
  };
  const [group, setGroup] = useState("");
  const [groupPresence, setGroupPresence] = useState([]);
  useEffect(() => {
    const list = group.split(",").map((x) => x.trim()).filter(Boolean);
    if (list.length === 0) { setGroupPresence([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/contacts/match", { contacts: list.map((v) => (v.includes("@") ? { email: v } : { tel: v })) });
        setGroupPresence(data.members || []);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [group]);
  const startGroup = (video) => {
    const list = group.split(",").map((x) => x.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Add at least one member (comma-separated)"); return; }
    call?.startGroupCall?.(list, video);
  };
  const sendCallback = async (id) => { try { await api.post(`/calls/${id}/callback`, { text: "" }); toast.success("Sent: I'll call back shortly"); } catch { toast.error("Couldn't send"); } };
  const fmtDur = (s) => (s > 0 ? (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`) : "");
  const relTime = (iso) => { if (!iso) return ""; const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000; if (diff < 60) return "just now"; if (diff < 3600) return `${Math.floor(diff / 60)}m ago`; if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`; return d.toLocaleDateString(); };
  return (
    <div className="mx-auto max-w-md space-y-4" data-testid="free-call-panel">
      <div className="rounded-3xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Call over the internet</h2>
            <p className="text-sm text-muted-foreground">Ring any iYeneh member — no phone minutes used.</p>
          </div>
          <span data-testid="free-call-rate" className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${!rateReady ? "bg-secondary text-muted-foreground" : cpm > 0 ? "bg-accent text-accent-foreground" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"}`}>{rateLabel}</span>
        </div>
        <input data-testid="free-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Member phone or email"
          className="mt-4 w-full rounded-xl border border-input bg-background px-3 py-3 outline-none focus:border-primary" />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button data-testid="free-audio-call" onClick={() => start(false)} className="flex items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"><Phone size={18} weight="fill" /> Audio</button>
          <button data-testid="free-video-call" onClick={() => start(true)} className="flex items-center justify-center gap-2 rounded-full bg-[#08111F] py-3 font-semibold text-white transition-transform hover:scale-[1.02]"><VideoCamera size={18} weight="fill" /> Video</button>
        </div>
        <button data-testid="free-link-call" onClick={() => call?.startLinkCall?.(true)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary"><LinkSimple size={16} /> Create a shareable call link</button>
        <p className="mt-3 text-center text-xs text-muted-foreground">{!rateReady ? "\u00a0" : cpm > 0 ? `Billed ${rateLabel} from your wallet. You must have funds to call.` : "Free for members with wallet funds."}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4" data-testid="group-call">
        <h3 className="mb-1 text-sm font-semibold">Group call</h3>
        <p className="mb-3 text-xs text-muted-foreground">Ring several members into one room. Separate phones/emails with commas.</p>
        <input data-testid="group-recipients" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="alex@x.com, +12025551234, maria@x.com"
          className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
        {groupPresence.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="group-presence">
            {groupPresence.map((m) => (
              <span key={m.user_id} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">
                <span className={`h-2 w-2 rounded-full ${m.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                {m.name}{m.online ? "" : " · offline"}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button data-testid="group-audio" onClick={() => startGroup(false)} className="flex items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary"><Phone size={16} weight="fill" /> Audio group</button>
          <button data-testid="group-video" onClick={() => startGroup(true)} className="flex items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary"><VideoCamera size={16} weight="fill" /> Video group</button>
        </div>
      </div>

      <IyenehContacts smsEnabled />

      <div className="rounded-2xl border border-border bg-card p-4" data-testid="call-history">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent internet calls</h3>
        {history.length === 0 && <p className="py-3 text-center text-sm text-muted-foreground">No calls yet</p>}
        <div className="space-y-1">
          {history.slice(0, 12).map((h) => (
            <div key={h.id} className="flex items-center gap-3 rounded-xl px-2 py-2" data-testid={`history-${h.id}`}>
              {h.missed
                ? <PhoneSlash size={18} weight="fill" className="text-destructive" />
                : h.direction === "outgoing"
                  ? <ArrowUpRight size={18} weight="bold" className="text-primary" />
                  : <ArrowDownLeft size={18} weight="bold" className="text-emerald-500" />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{h.other_name || "Unknown"} · {h.video ? "video" : "audio"}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {h.missed ? "Missed" : h.declined ? "Declined" : h.answered ? "Connected" : "No answer"}
                  {h.duration_seconds ? ` · ${fmtDur(h.duration_seconds)}` : ""}
                  {h.direction === "outgoing" && h.cost_cents ? ` · ${money(h.cost_cents)}` : ""}
                  {` · ${relTime(h.created_at)}`}
                </p>
                {h.callback_note && <p className="truncate text-xs text-primary">↩ {h.direction === "incoming" ? "You" : h.callback_note.from}: {h.callback_note.text}</p>}
              </div>
              {h.direction === "incoming" && (h.missed || h.declined) && (
                <button data-testid={`history-callback-${h.id}`} onClick={() => sendCallback(h.id)} className="whitespace-nowrap rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-transform hover:scale-105">I'll call back</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
